import React, { useState, useEffect, useRef } from 'react';
import { db, auth, functions } from '../../firebase/config';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, orderBy, updateDoc, doc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage'; 
import { Link } from 'react-router-dom'; // 🚨 NEW: Imported Link for deep routing

export default function Inbox() {
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false); 
  
  // Trade Engine State
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [myInventory, setMyInventory] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);

  // --- MEETUP SCHEDULER STATE ---
  const [showMeetupModal, setShowMeetupModal] = useState(false);
  const [meetupDate, setMeetupDate] = useState('');
  const [meetupTime, setMeetupTime] = useState('');
  const [meetupLocation, setMeetupLocation] = useState(''); 

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const hasSentOffer = useRef(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!auth.currentUser) return;
    const q = query(collection(db, 'chats'), where('participants', 'array-contains', auth.currentUser.uid));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const chatList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setChats(chatList);

      const pendingOffer = sessionStorage.getItem('pendingOffer');
      if (pendingOffer && chatList.length > 0 && !hasSentOffer.current) {
        const targetChat = chatList.find(c => pendingOffer.includes(c.itemTitle));
        if (targetChat) {
          hasSentOffer.current = true;
          setActiveChat(targetChat);
          
          const amountMatch = pendingOffer.match(/\$([0-9.]+)/);
          const offerAmount = amountMatch ? Number(amountMatch[1]) : 0;

          // Don't send ISO text as a formal offer widget
          const isIsoText = pendingOffer.includes("I have the hardware you are looking for");

          addDoc(collection(db, `chats/${targetChat.id}/messages`), {
            text: pendingOffer, 
            isOffer: !isIsoText, 
            offerAmount: offerAmount, 
            offerStatus: isIsoText ? null : 'pending', 
            senderId: auth.currentUser.uid, 
            createdAt: serverTimestamp()
          }).then(() => { sessionStorage.removeItem('pendingOffer'); });
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!activeChat) return;
    const q = query(collection(db, `chats/${activeChat.id}/messages`), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => { setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); });
    return () => unsubscribe();
  }, [activeChat]);

  const fetchMyInventory = async () => {
    if (!auth.currentUser) return;
    setLoadingInventory(true);
    try {
      const q = query(collection(db, 'listings'), where('sellerId', '==', auth.currentUser.uid), where('status', '==', 'active'));
      const snap = await getDocs(q);
      setMyInventory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setShowTradeModal(true);
    } catch (error) {
      console.error("Error fetching inventory:", error);
      alert("Could not load your gear for a trade.");
    } finally {
      setLoadingInventory(false);
    }
  };

  const handleSendTradeOffer = async (tradeItem) => {
    if (!activeChat) return;
    try {
      await addDoc(collection(db, `chats/${activeChat.id}/messages`), {
        isTradeOffer: true,
        tradeItemTitle: tradeItem.title,
        tradeItemId: tradeItem.id,
        tradeItemImage: tradeItem.mainImage || tradeItem.imageUrls?.[0] || '',
        offerStatus: 'pending',
        senderId: auth.currentUser.uid,
        createdAt: serverTimestamp()
      });
      setShowTradeModal(false);
    } catch (error) {
      console.error("Trade offer error:", error);
      alert("Failed to send trade offer.");
    }
  };

  const handleSendMeetupProposal = async (e) => {
    e.preventDefault();
    if (!activeChat || !meetupDate || !meetupTime || !meetupLocation) return alert("Please fill out all meetup details.");
    
    try {
      await addDoc(collection(db, `chats/${activeChat.id}/messages`), {
        isMeetupProposal: true,
        meetupDate,
        meetupTime,
        meetupLocation,
        offerStatus: 'pending',
        senderId: auth.currentUser.uid,
        createdAt: serverTimestamp()
      });
      setShowMeetupModal(false);
      setMeetupDate('');
      setMeetupTime('');
      setMeetupLocation('');
    } catch (error) {
      console.error("Meetup proposal error:", error);
      alert("Failed to send meetup request.");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeChat) return;
    await addDoc(collection(db, `chats/${activeChat.id}/messages`), {
      text: newMessage, isOffer: false, senderId: auth.currentUser.uid, createdAt: serverTimestamp()
    });
    setNewMessage('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeChat) return;

    if (file.size > 10 * 1024 * 1024) {
      return alert("File is too large. Please select an image under 10MB.");
    }

    setUploadingImage(true);
    try {
      const storage = getStorage();
      const fileRef = ref(storage, `chat_images/${activeChat.id}/${Date.now()}_${file.name}`);
      
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      await addDoc(collection(db, `chats/${activeChat.id}/messages`), {
        imageUrl: url, 
        isOffer: false,
        senderId: auth.currentUser.uid,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to send image.");
    } finally {
      setUploadingImage(false);
      e.target.value = null; 
    }
  };

  const handleOfferAction = async (messageId, action) => {
    try {
      await updateDoc(doc(db, `chats/${activeChat.id}/messages`, messageId), { offerStatus: action });
    } catch (error) { alert("Failed to update status."); }
  };

  const handleCheckoutOffer = async (offerAmount) => {
    setProcessingCheckout(true);
    try {
      const createCheckout = httpsCallable(functions, 'createPayPalOrder');
      const result = await createCheckout({ itemId: activeChat.itemId, offerAmount: offerAmount });
      window.location.assign(result.data.url);
    } catch (error) { 
      alert("Failed to initiate secure checkout."); 
      setProcessingCheckout(false); 
    }
  };

  const showListPanel = !isMobile || (isMobile && !activeChat);
  const showChatPanel = !isMobile || (isMobile && activeChat);

  return (
    <div style={{ maxWidth: '1200px', margin: isMobile ? '0' : '20px auto', display: 'flex', height: isMobile ? 'calc(100vh - 60px)' : '75vh', backgroundColor: 'white', borderRadius: isMobile ? '0' : '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', overflow: 'hidden', border: isMobile ? 'none' : '1px solid #eee' }}>
      
      <div style={{ display: showListPanel ? 'flex' : 'none', width: isMobile ? '100%' : '350px', borderRight: isMobile ? 'none' : '1px solid #eee', backgroundColor: '#f8f9fa', flexDirection: 'column' }}>
        <div style={{ padding: '25px 20px', backgroundColor: '#1a1a1a', color: 'white' }}>
          <h2 style={{ margin: 0, fontSize: '20px', letterSpacing: '0.5px' }}>Negotiations</h2>
        </div>
        
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {chats.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#adb5bd' }}>
              <p style={{ fontSize: '14px', margin: 0 }}>No active conversations yet.</p>
            </div>
          ) : (
            chats.map(chat => {
              // Gracefully handle older chats that might not have a direct buyerId mapping
              const isUserBuyer = chat.buyerId ? chat.buyerId === auth.currentUser.uid : chat.participants[0] === auth.currentUser.uid;
              
              return (
                <div 
                  key={chat.id} 
                  onClick={() => setActiveChat(chat)}
                  style={{ padding: '20px', borderBottom: '1px solid #eee', cursor: 'pointer', backgroundColor: activeChat?.id === chat.id ? 'white' : 'transparent', borderLeft: activeChat?.id === chat.id && !isMobile ? '4px solid #007bff' : '4px solid transparent' }}
                >
                  <h4 style={{ margin: '0 0 5px 0', color: activeChat?.id === chat.id && !isMobile ? '#007bff' : '#1a1a1a', fontSize: '15px' }}>{chat.itemTitle}</h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#6c757d', fontWeight: 'bold', textTransform: 'uppercase' }}>
                    {isUserBuyer ? '🛒 Buying' : '💰 Selling'}
                  </p>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div style={{ display: showChatPanel ? 'flex' : 'none', flex: 1, flexDirection: 'column', backgroundColor: '#fff', width: isMobile ? '100%' : 'auto', position: 'relative' }}>
        {activeChat ? (
          <>
            <div style={{ padding: '20px', borderBottom: '1px solid #eee', backgroundColor: '#fff', display: 'flex', alignItems: 'center', gap: '15px' }}>
              {isMobile && (
                <button onClick={() => setActiveChat(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', padding: '0' }}>
                  ⬅️
                </button>
              )}
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#1a1a1a' }}>{activeChat.itemTitle}</h3>
                <span style={{ fontSize: '12px', color: '#28a745', fontWeight: 'bold' }}>Secure Session</span>
              </div>
            </div>

            <div style={{ flex: 1, padding: isMobile ? '15px' : '30px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '15px', backgroundColor: '#f0f2f5' }}>
              {messages.map(msg => {
                const isMe = msg.senderId === auth.currentUser.uid;
                const isBuyer = activeChat.buyerId ? activeChat.buyerId === auth.currentUser.uid : activeChat.participants[0] === auth.currentUser.uid;

                if (msg.isMeetupProposal) {
                  return (
                    <div key={msg.id} style={{ alignSelf: 'center', width: '100%', maxWidth: '400px', margin: '10px 0' }}>
                      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '2px solid #c3e6cb', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                        <div style={{ backgroundColor: '#d4edda', padding: '15px', textAlign: 'center', borderBottom: '1px solid #c3e6cb' }}>
                          <h4 style={{ margin: '0 0 5px 0', color: '#155724', textTransform: 'uppercase', fontSize: '12px' }}>📅 Local Meetup Proposal</h4>
                        </div>
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                          <p style={{ margin: '0 0 5px 0', fontSize: '18px', fontWeight: 'bold', color: '#1a1a1a' }}>{msg.meetupDate} at {msg.meetupTime}</p>
                          <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#6c757d' }}>📍 {msg.meetupLocation}</p>
                          
                          {msg.offerStatus === 'pending' && (
                            <>
                              {!isMe ? (
                                <div style={{ display: 'flex', gap: '10px', flexDirection: isMobile ? 'column' : 'row' }}>
                                  <button onClick={() => handleOfferAction(msg.id, 'declined')} style={{ flex: 1, padding: '10px', backgroundColor: '#f8f9fa', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '6px', fontWeight: 'bold' }}>Decline</button>
                                  <button onClick={() => handleOfferAction(msg.id, 'accepted')} style={{ flex: 1, padding: '10px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold' }}>Confirm Meetup</button>
                                </div>
                              ) : <p style={{ margin: 0, color: '#6c757d', fontWeight: 'bold', fontSize: '14px' }}>⏳ Waiting for response...</p>}
                            </>
                          )}
                          {msg.offerStatus === 'declined' && <p style={{ margin: 0, color: '#dc3545', fontWeight: 'bold', fontSize: '15px' }}>🚫 Meetup Declined</p>}
                          
                          {/* 🚨 NEW: Added Review Engine link upon successful meetup */}
                          {msg.offerStatus === 'accepted' && (
                            <>
                              <p style={{ margin: '0 0 10px 0', color: '#28a745', fontWeight: 'bold', fontSize: '16px' }}>✅ Meetup Confirmed! See you there.</p>
                              {isBuyer && (
                                <Link to={`/review/${activeChat.itemId}`} style={{ textDecoration: 'none' }}>
                                  <button style={{ width: '100%', padding: '12px', backgroundColor: '#fbbf24', color: '#0f172a', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', marginTop: '5px' }}>
                                    ⭐ Leave a Review
                                  </button>
                                </Link>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                if (msg.isTradeOffer) {
                  return (
                    <div key={msg.id} style={{ alignSelf: 'center', width: '100%', maxWidth: '400px', margin: '10px 0' }}>
                      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '2px solid #b8daff', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                        <div style={{ backgroundColor: '#cce5ff', padding: '15px', textAlign: 'center', borderBottom: '1px solid #b8daff' }}>
                          <h4 style={{ margin: '0 0 5px 0', color: '#004085', textTransform: 'uppercase', fontSize: '12px' }}>🔄 Hardware Swap Offer</h4>
                        </div>
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                          <img src={msg.tradeItemImage || 'https://via.placeholder.com/100'} alt="Trade Item" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', marginBottom: '10px' }} />
                          <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#1a1a1a' }}>{msg.tradeItemTitle}</h4>
                          
                          {msg.offerStatus === 'pending' && (
                            <>
                              {!isMe ? (
                                <div style={{ display: 'flex', gap: '10px', flexDirection: isMobile ? 'column' : 'row' }}>
                                  <button onClick={() => handleOfferAction(msg.id, 'declined')} style={{ flex: 1, padding: '10px', backgroundColor: '#f8f9fa', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '6px', fontWeight: 'bold' }}>Pass</button>
                                  <button onClick={() => handleOfferAction(msg.id, 'accepted')} style={{ flex: 1, padding: '10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold' }}>Accept Trade</button>
                                </div>
                              ) : <p style={{ margin: 0, color: '#6c757d', fontWeight: 'bold', fontSize: '14px' }}>⏳ Waiting for seller...</p>}
                            </>
                          )}
                          {msg.offerStatus === 'declined' && <p style={{ margin: 0, color: '#dc3545', fontWeight: 'bold', fontSize: '15px' }}>🚫 Trade Declined</p>}
                          
                          {/* 🚨 NEW: Added Review Engine link upon successful trade */}
                          {msg.offerStatus === 'accepted' && (
                            <>
                              <p style={{ margin: '0 0 10px 0', color: '#28a745', fontWeight: 'bold', fontSize: '16px' }}>✅ Trade Accepted! Exchange details in chat.</p>
                              {isBuyer && (
                                <Link to={`/review/${activeChat.itemId}`} style={{ textDecoration: 'none' }}>
                                  <button style={{ width: '100%', padding: '12px', backgroundColor: '#fbbf24', color: '#0f172a', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', marginTop: '5px' }}>
                                    ⭐ Leave a Review
                                  </button>
                                </Link>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                if (msg.isOffer) {
                  return (
                    <div key={msg.id} style={{ alignSelf: 'center', width: '100%', maxWidth: '400px', margin: '10px 0' }}>
                      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '2px solid #ffeeba', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                        <div style={{ backgroundColor: '#fff3cd', padding: '15px', textAlign: 'center', borderBottom: '1px solid #ffeeba' }}>
                          <h4 style={{ margin: '0 0 5px 0', color: '#856404', textTransform: 'uppercase', fontSize: '12px' }}>Formal Offer</h4>
                          <span style={{ fontSize: '32px', fontWeight: '900', color: '#1a1a1a' }}>${msg.offerAmount?.toFixed(2)}</span>
                        </div>
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                          {msg.offerStatus === 'pending' && (
                            <>
                              {!isMe ? (
                                <div style={{ display: 'flex', gap: '10px', flexDirection: isMobile ? 'column' : 'row' }}>
                                  <button onClick={() => handleOfferAction(msg.id, 'declined')} style={{ flex: 1, padding: '10px', backgroundColor: '#f8f9fa', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '6px', fontWeight: 'bold' }}>Decline</button>
                                  <button onClick={() => handleOfferAction(msg.id, 'accepted')} style={{ flex: 1, padding: '10px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold' }}>Accept Offer</button>
                                </div>
                              ) : <p style={{ margin: 0, color: '#6c757d', fontWeight: 'bold', fontSize: '14px' }}>⏳ Waiting for seller...</p>}
                            </>
                          )}
                          {msg.offerStatus === 'declined' && <p style={{ margin: 0, color: '#dc3545', fontWeight: 'bold', fontSize: '15px' }}>🚫 Offer Declined</p>}
                          
                          {/* 🚨 NEW: Grouped Checkout & Review Engine Action Flow */}
                          {msg.offerStatus === 'accepted' && (
                            <>
                              <p style={{ margin: '0 0 15px 0', color: '#28a745', fontWeight: 'bold', fontSize: '16px' }}>✅ Offer Accepted!</p>
                              {isBuyer && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                  <button onClick={() => handleCheckoutOffer(msg.offerAmount)} disabled={processingCheckout} style={{ width: '100%', padding: '12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '900', cursor: processingCheckout ? 'not-allowed' : 'pointer' }}>
                                    {processingCheckout ? 'Routing...' : '💳 Complete Purchase Now'}
                                  </button>
                                  <Link to={`/review/${activeChat.itemId}`} style={{ textDecoration: 'none' }}>
                                    <button style={{ width: '100%', padding: '12px', backgroundColor: '#fbbf24', color: '#0f172a', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer' }}>
                                      ⭐ Leave a Review
                                    </button>
                                  </Link>
                                </div>
                              )}
                              {!isBuyer && <p style={{ margin: 0, color: '#6c757d', fontSize: '13px' }}>Waiting for buyer to complete payment.</p>}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={msg.id} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                    <div style={{ padding: msg.imageUrl ? '6px' : '12px 18px', borderRadius: isMe ? '18px 18px 0 18px' : '18px 18px 18px 0', backgroundColor: isMe ? '#007bff' : '#fff', color: isMe ? 'white' : '#1a1a1a', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                      {msg.imageUrl ? (
                        <a href={msg.imageUrl} target="_blank" rel="noreferrer">
                          <img src={msg.imageUrl} alt="Attachment" style={{ width: '100%', maxWidth: '250px', borderRadius: '14px', display: 'block', objectFit: 'cover' }} />
                        </a>
                      ) : (
                        msg.text
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <form onSubmit={handleSendMessage} style={{ padding: '15px', borderTop: '1px solid #eee', display: 'flex', gap: '10px', backgroundColor: '#fff', alignItems: 'center' }}>
              
              <label style={{ cursor: uploadingImage ? 'not-allowed' : 'pointer', fontSize: '22px', padding: '0 5px', opacity: uploadingImage ? 0.5 : 1, transition: 'transform 0.2s' }} title="Attach Photo" onMouseOver={e => e.currentTarget.style.transform = 'scale(1.1)'} onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}>
                📎
                <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploadingImage} />
              </label>

              <button 
                type="button" 
                onClick={fetchMyInventory}
                disabled={loadingInventory}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '0 5px', transition: 'transform 0.2s' }} 
                title="Offer a Trade"
                onMouseOver={e => e.currentTarget.style.transform = 'scale(1.1)'} 
                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                🔄
              </button>

              <button 
                type="button" 
                onClick={() => setShowMeetupModal(true)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '0 5px', transition: 'transform 0.2s' }} 
                title="Schedule Meetup"
                onMouseOver={e => e.currentTarget.style.transform = 'scale(1.1)'} 
                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                📅
              </button>

              <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder={uploadingImage ? "Uploading image..." : "Type a message..."} disabled={uploadingImage} style={{ flex: 1, padding: '12px 20px', borderRadius: '50px', border: '1px solid #e9ecef', backgroundColor: '#f8f9fa', outline: 'none', fontSize: '15px' }} />
              <button type="submit" disabled={uploadingImage} style={{ padding: '0 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '50px', fontWeight: '900', cursor: uploadingImage ? 'not-allowed' : 'pointer' }}>Send</button>
            </form>

            {/* --- MEETUP SCHEDULER MODAL --- */}
            {showMeetupModal && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100, padding: '20px' }}>
                <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', width: '100%', maxWidth: '400px' }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '20px' }}>Schedule Local Meetup</h3>
                  <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px' }}>Propose a time and a verified safe public location.</p>
                  
                  <form onSubmit={handleSendMeetupProposal} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Date</label>
                        <input type="date" value={meetupDate} onChange={e => setMeetupDate(e.target.value)} required style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Time</label>
                        <input type="time" value={meetupTime} onChange={e => setMeetupTime(e.target.value)} required style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Meetup Location (Public & Safe)</label>
                      <input 
                        type="text" 
                        placeholder="e.g. Downtown Police Station, Starbucks..." 
                        value={meetupLocation} 
                        onChange={e => setMeetupLocation(e.target.value)} 
                        required 
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box' }} 
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <button type="button" onClick={() => setShowMeetupModal(false)} style={{ flex: 1, padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Cancel</button>
                      <button type="submit" style={{ flex: 1, padding: '12px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Send Proposal</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* TRADE INVENTORY MODAL */}
            {showTradeModal && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100, padding: '20px' }}>
                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', width: '100%', maxWidth: '500px', maxHeight: '80%', display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '20px' }}>Select an Item to Trade</h3>
                  <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px' }}>Offer a 1-for-1 swap using gear from your active inventory.</p>
                  
                  <div style={{ overflowY: 'auto', flex: 1, border: '1px solid #eee', borderRadius: '8px', padding: '10px' }}>
                    {myInventory.length === 0 ? (
                      <p style={{ textAlign: 'center', color: '#888', marginTop: '20px' }}>You don't have any active listings to trade.</p>
                    ) : (
                      myInventory.map(item => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '10px', borderBottom: '1px solid #eee' }}>
                          <img src={item.mainImage || item.imageUrls?.[0] || 'https://via.placeholder.com/50'} alt={item.title} style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }} />
                          <div style={{ flex: 1 }}>
                            <h4 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>{item.title}</h4>
                            <span style={{ fontSize: '12px', color: '#28a745', fontWeight: 'bold' }}>${item.price} Value</span>
                          </div>
                          <button onClick={() => handleSendTradeOffer(item)} style={{ padding: '8px 16px', backgroundColor: '#1a1a1a', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Offer This</button>
                        </div>
                      ))
                    )}
                  </div>
                  
                  <button onClick={() => setShowTradeModal(false)} style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#adb5bd' }}>
            <span style={{ fontSize: '50px', marginBottom: '20px' }}>💬</span>
            <p style={{ fontSize: '18px', fontWeight: '500' }}>Secure Negotiation Hub</p>
          </div>
        )}
      </div>
    </div>
  );
}